import React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { extendTailwindMerge } from 'tailwind-merge';
import If from '../If';
import { useAppSelector } from '../../redux/hooks';
import {
  selectProduct,
  selectSelectedShippingPriority,
  selectStoreData
} from '../../redux/slices/customizer/customizerSlice';

interface Props {
  openSummary: () => void;
  className?: string;
  layout?: 'stacked' | 'inline';
}

interface ShipDate {
  priority: string;
  label: string;
  date: string;
}

const twMerge = extendTailwindMerge({ prefix: 'tw-' });

const formatShortDate = (date: string | undefined) => {
  if (!date) return '';
  return moment(date).format('MMM. D') + '*';
};

const ShipDateSummary = ({ openSummary, className, layout = 'stacked' }: Props) => {
  const product = useAppSelector(selectProduct);
  const storeData = useAppSelector(selectStoreData);
  const selectedShippingPriority = useAppSelector(selectSelectedShippingPriority);
  const shippingOptions = product?.shipping_options;

  const handleClick = () => {
    (window as any).__highlightShipDate = true;
    openSummary();
    setTimeout(() => {
      window.dispatchEvent(new Event('highlight-ship-date'));
    }, 100);
  };

  // Same labels and order as the "Choose Your Shipping Date" section in Summary
  const dates: ShipDate[] = [];
  if (shippingOptions?.same_day && shippingOptions?.next_day) {
    const earliest = moment(shippingOptions.same_day).isBefore(moment(shippingOptions.next_day))
      ? shippingOptions.same_day
      : shippingOptions.next_day;
    dates.push({ priority: 'standard', label: 'Standard', date: earliest });
  } else {
    if (shippingOptions?.same_day)
      dates.push({ priority: 'same_day', label: 'Same Day', date: shippingOptions.same_day });
    if (shippingOptions?.next_day)
      dates.push({ priority: 'next_day', label: 'Next Day', date: shippingOptions.next_day });
  }
  if (shippingOptions?.standard) {
    dates.push({ priority: 'standard', label: 'Without Rush', date: shippingOptions.standard });
  }
  if (shippingOptions?.rush) {
    const fee = shippingOptions.rush_fee ? ` (+ $${shippingOptions.rush_fee})` : '';
    dates.push({ priority: 'rush', label: `With Rush${fee}`, date: shippingOptions.rush });
  }

  const hasChoice = dates.length > 1;

  // Summary sets the default priority, but it isn't mounted until its step is visited
  const offered = (priority?: string) => dates.some((d) => d.priority === priority);
  const activePriority = offered(selectedShippingPriority)
    ? selectedShippingPriority
    : product?.shipping_priority === 'rush' && offered('rush')
      ? 'rush'
      : ['standard', 'same_day', 'next_day', 'rush'].find(offered);

  return (
    <If condition={dates.length > 0 && !!storeData?.can_select_shipping_priority}>
      <div
        role="button"
        tabIndex={0}
        className={twMerge(
          'tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-cursor-pointer',
          className
        )}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <div className="tw-text-sm tw-text-gray-600">
          {hasChoice ? 'Choose Ship Date' : 'Ship Date'}
          <i className="fa fa-chevron-right tw-text-[0.65em] tw-ml-1.5 tw-align-middle" aria-hidden />
        </div>
        <div
          className={clsx(
            'tw-flex tw-text-base',
            layout === 'inline' ? 'tw-flex-row tw-flex-wrap tw-justify-center tw-gap-x-4' : 'tw-flex-col'
          )}
        >
          {dates.map(({ priority, label, date }, i) => {
            const isSelected = hasChoice && activePriority === priority;
            return (
              <React.Fragment key={`${priority}-${label}`}>
                <If condition={layout === 'inline' && i > 0}>
                  <span className="tw-self-center tw-w-px tw-h-4 tw-bg-gray-500" aria-hidden />
                </If>
                <div className={clsx({ 'tw-font-medium': isSelected })} style={{ color: '#000000' }}>
                  <If condition={isSelected}>
                    <i className="fa fa-check tw-mr-1" aria-hidden />
                  </If>
                  {label} - {formatShortDate(date)}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </If>
  );
};

export default ShipDateSummary;
